import type { ResolvedDocument, DrawingEntity, PolylineEntity } from '../contract/types.js';
import { toDrawingSpace } from '../transform/space.js';
// The same advance measurement the overlay draws to. Implemented once: an
// overlay and a DXF that measured a string separately would drift, and the
// drift would look like a conversion error in whichever one was read second.
import { advanceIsEstimated, advanceOf } from '../overlay/shapes.js';
import { TEXT_OUTLINE_LAYER } from './outline.js';

/**
 * A DXF R2018 writer, written by hand rather than taken from a library.
 *
 * Three reasons, all of them load-bearing:
 *
 *  1. **Determinism.** DXF libraries regenerate a document identifier on every
 *     write and embed wall-clock timestamps, so the same drawing written twice
 *     produces different bytes. Writing the file ourselves means the bytes are
 *     stable and a content hash means something.
 *  2. **Portability.** This runs unchanged in the browser, so a drawing never
 *     has to leave the viewer's machine.
 *  3. **No new dependency** in the deployed artefact.
 *
 * Correctness is not asserted, it is verified: the kernel reads every file back
 * with ezdxf and runs its audit. If the industry-standard reader accepts the
 * file with zero audit errors, a CAD application will too.
 */

const NL = '\r\n';

/** DXF is R2018 internally AC1032. */
export const DXF_VERSION = 'AC1032';

/** $INSUNITS 4 = millimetres. The common default is metres, which would make every drawing 1000x oversized. */
const INSUNITS_MILLIMETRES = 4;

/** $MEASUREMENT 1 = metric. Independent of $INSUNITS and not synchronised automatically. */
const MEASUREMENT_METRIC = 1;

/**
 * The text style every TEXT and MTEXT entity names.
 *
 * **A DXF carries characters, not glyphs.** It names a font and the CAD
 * application supplies it. `txt.shx` — the conventional default, and what this
 * writer named until now — is a Latin stroke font with no CJK glyphs and no
 * companion big font, so a title block reading 図番 arrived in AutoCAD as `??`.
 * The characters were in the file the whole time, byte-perfect; the file had
 * told the reader to draw them with something that could not. Nothing in this
 * repository caught it, because every check looked at the data: the acceptance
 * gate scores extraction, the round trip compares geometry, and ezdxf reads the
 * string back exactly as written. A correct file that cannot be displayed is
 * indistinguishable from a broken one to the person opening it.
 *
 * So the default names a TrueType font that covers Japanese. MS Gothic is the
 * choice because it is present on every Japanese Windows installation, which is
 * where these files get opened — and it covers Latin too, so one style serves
 * the whole drawing and no drawing ends up with two typefaces in it.
 */
export interface DxfTextStyle {
  /** Symbol-table name the entities reference. */
  readonly name: string;
  /** Group 3, the font *file*: a `.ttf`/`.ttc`/`.otf`, or an `.shx` stroke font. */
  readonly font: string;
  /** Group 1000 under the ACAD application: the typeface name AutoCAD resolves by. */
  readonly typeface: string;
  /** Group 4, the Asian big-font `.shx`. Only meaningful when `font` is an `.shx`. */
  readonly bigFont: string;
  /**
   * Which script the style is for.
   *
   * This is the part that matters when the named font is **absent**. A reader
   * that cannot find `msgothic.ttc` has to substitute something, and the charset
   * is the only thing in the file that tells it *what kind* of font to reach for.
   * Declaring `ansi` on a Japanese style invites a substitution onto a Latin
   * face, which is a `?` per character — the symptom this whole style exists to
   * fix, reintroduced one level down.
   */
  readonly charset: 'ansi' | 'shiftJis';
}

export const CJK_TEXT_STYLE: DxfTextStyle = {
  name: 'AIONA_JP',
  font: 'msgothic.ttc',
  typeface: 'MS Gothic',
  bigFont: '',
  charset: 'shiftJis',
};

/**
 * The fonts a Japanese drawing is realistically opened with, offered as a choice.
 *
 * A DXF names a font; it cannot carry one. So whether Japanese appears at all is
 * decided by the machine the file is opened on, and **no value this writer can
 * emit makes a reader draw a glyph it does not have.** That is a property of the
 * format, not a defect we can close.
 *
 * What the writer *can* do is name a font the reader is likely to actually have,
 * which is a different answer per platform: MS Gothic is guaranteed on Japanese
 * Windows and absent from macOS; Yu Gothic ships with Windows 10 worldwide;
 * Hiragino is macOS only; Noto is what a Linux box or a server-side viewer tends
 * to have. Offering the list beats guessing on the operator's behalf, and it is
 * the honest shape of the problem: we do not know what is installed where this
 * file is going, and the operator does.
 */
export const CJK_FONT_PRESETS: readonly DxfTextStyle[] = [
  CJK_TEXT_STYLE,
  { name: 'AIONA_JP', font: 'YuGothM.ttc', typeface: 'Yu Gothic Medium', bigFont: '', charset: 'shiftJis' },
  { name: 'AIONA_JP', font: 'meiryo.ttc', typeface: 'Meiryo', bigFont: '', charset: 'shiftJis' },
  { name: 'AIONA_JP', font: 'HiraginoSans-W3.ttc', typeface: 'Hiragino Sans', bigFont: '', charset: 'shiftJis' },
  {
    name: 'AIONA_JP',
    font: 'NotoSansCJKjp-Regular.otf',
    typeface: 'Noto Sans CJK JP',
    bigFont: '',
    charset: 'shiftJis',
  },
  /**
   * The stroke-font route, for Japanese AutoCAD specifically. `txt.shx` draws the
   * Latin and the big font draws everything outside it — the mechanism that
   * predates TrueType styles and the only one some older installations honour.
   * It is last because a missing `.shx` is a harder failure than a missing TTF:
   * AutoCAD prompts for a substitute instead of quietly picking one.
   */
  { name: 'AIONA_JP', font: 'txt', typeface: '', bigFont: 'extfont2.shx', charset: 'shiftJis' },
];

/**
 * `Standard` stays in the table pointing at `txt` regardless. Readers and the
 * DIMSTYLE record expect a style by that name to exist, and leaving it alone
 * means this change cannot alter how anything that already worked is drawn.
 */
const STANDARD_TEXT_STYLE: DxfTextStyle = {
  name: 'Standard',
  font: 'txt',
  typeface: '',
  bigFont: '',
  charset: 'ansi',
};

/**
 * The extended font data flags, group 1071.
 *
 * ezdxf writes a bare 34 here and its own source calls that "unknown default
 * flags". 34 is `0x22`, and the field behaves as a packed Windows `LOGFONT`:
 * the low byte is `lfPitchAndFamily` — `0x22` being variable pitch with the
 * swiss family, what a normal proportional face gets — and the next byte up is
 * `lfCharSet`. ezdxf's own `ITALIC` and `BOLD` constants sit at bits 24 and 25,
 * which is consistent with the two low bytes being the LOGFONT pair and nothing
 * else competing for them.
 *
 * **Confidence, stated because it is low.** libdxfrw describes 1071 as packing
 * "pitch and family, charset, and italic and bold flags", which establishes that
 * a charset is in there somewhere; `0x22` matching Arial's FF_SWISS with variable
 * pitch supports the low byte being `lfPitchAndFamily`. The *position* of the
 * charset byte is not verified — ANSI is zero, so a real file's zero there is
 * indistinguishable from an unused byte, and no AutoCAD-written DXF was available
 * to check against.
 *
 * What is measured is that the value is **inert in every instrument this
 * repository owns**: 34, 32802, and no extended data at all produce a clean audit
 * and a byte-identical render from ezdxf. So this is not the fix for the reported
 * `??` and must not be sold as one. It is kept because the field is XDATA under
 * the ACAD application — a reader that does not understand it ignores it — so
 * being wrong costs nothing beyond the bare 34 it replaces, and being right gives
 * a substituting reader the one hint in the file about what kind of font to reach
 * for. A cheap bet with a bounded loss, not a solution.
 */
const CHARSET_CODE = { ansi: 0, shiftJis: 128 } as const;
const PITCH_AND_FAMILY_PROPORTIONAL = 0x22;

function extendedFontFlags(style: DxfTextStyle): number {
  return (CHARSET_CODE[style.charset] << 8) | PITCH_AND_FAMILY_PROPORTIONAL;
}

/** Symbol-table names admit letters, digits and `$_-`; anything else is an audit error. */
const LEGAL_STYLE_NAME = /^[A-Za-z0-9$_-]+$/;

export interface WriteDxfOptions {
  /**
   * Overrides the text style. A client with a house font sets it here; the
   * default is {@link CJK_TEXT_STYLE}.
   */
  readonly textStyle?: DxfTextStyle;
  /**
   * Closed contours to draw on `TEXT_OUTLINE`, keyed by page index, from
   * {@link outlineDocument}.
   *
   * **Off unless supplied, and supplying nothing must change nothing.** These
   * are for the reader Finding 8 describes, which never reads a font name and so
   * can only draw geometry; everyone else wants the `TEXT` entities, which stay
   * exactly as they were. A file with this option unset is byte-identical to one
   * written before the option existed, and `writer.test.ts` asserts it.
   *
   * Passed in rather than computed here because the caller needs the other half
   * of the same operation — which characters had no glyph — and because a writer
   * that reaches for a font is a writer that needs one.
   */
  readonly textOutlines?: ReadonlyMap<number, readonly PolylineEntity[]>;
}

function checkTextStyle(style: DxfTextStyle): DxfTextStyle {
  if (!LEGAL_STYLE_NAME.test(style.name)) {
    throw new Error(`text style name must match ${LEGAL_STYLE_NAME.source}, got ${JSON.stringify(style.name)}`);
  }
  if (style.name.toUpperCase() === 'STANDARD') {
    throw new Error('text style must not be named Standard; Standard is reserved for the txt.shx fallback');
  }
  if (style.font.trim() === '') {
    throw new Error('text style must name a font file, otherwise the reader has nothing to draw the characters with');
  }
  return style;
}

/**
 * DXF lineweights are a fixed enumeration in hundredths of a millimetre.
 * An arbitrary value is an audit error, so a measured stroke width is snapped
 * to the nearest legal one.
 */
const LINEWEIGHTS = [
  0, 5, 9, 13, 15, 18, 20, 25, 30, 35, 40, 50, 53, 60, 70, 80, 90, 100, 106, 120, 140, 158, 200, 211,
] as const;

function snapLineweight(mm: number): number {
  const hundredths = Math.round(mm * 100);
  let best: number = LINEWEIGHTS[0];
  let bestDelta = Infinity;
  for (const w of LINEWEIGHTS) {
    const d = Math.abs(w - hundredths);
    if (d < bestDelta) {
      bestDelta = d;
      best = w;
    }
  }
  return best;
}

/** Fixed decimal precision keeps output byte-identical between runs. */
function num(n: number): string {
  if (!Number.isFinite(n)) throw new Error(`non-finite coordinate: ${n}`);
  let s = n.toFixed(9);
  if (s.includes('.')) s = s.replace(/0+$/, '');
  if (s.endsWith('.')) s += '0';
  return s === '-0.0' || s === '-0' ? '0.0' : s;
}

class Builder {
  private readonly parts: string[] = [];
  private handle = 0x100;

  nextHandle(): string {
    return (this.handle++).toString(16).toUpperCase();
  }

  tag(code: number, value: string | number): this {
    this.parts.push(String(code), NL, String(value), NL);
    return this;
  }

  coord(code: number, value: number): this {
    return this.tag(code, num(value));
  }

  toString(): string {
    return this.parts.join('');
  }
}

/**
 * Writes a DXF.
 *
 * The parameter type is `ResolvedDocument`, not `DrawingDocument`. A document
 * whose physical scale could not be determined is a different type and cannot
 * be passed here — so "never write a DXF at unknown scale" is a guarantee the
 * compiler enforces, rather than a runtime branch a later change could delete.
 */
export function writeDxf(doc: ResolvedDocument, options: WriteDxfOptions = {}): string {
  assertScaleResolved(doc);

  const textStyle = checkTextStyle(options.textStyle ?? CJK_TEXT_STYLE);
  const b = new Builder();
  const layers = collectLayers(doc, options.textOutlines);

  // Handles are allocated in a fixed order, so the same input always produces
  // the same file.
  const rootDictHandle = b.nextHandle();
  const groupDictHandle = b.nextHandle();
  const modelSpaceHandle = b.nextHandle();
  const paperSpaceHandle = b.nextHandle();

  writeHeader(b);
  writeClasses(b);
  writeTables(b, layers, modelSpaceHandle, paperSpaceHandle, textStyle);
  writeBlocks(b, modelSpaceHandle, paperSpaceHandle);

  // Entities are held in PDF points so the overlay stays aligned to the original
  // page. The transform into drawing millimetres is applied here, at the one
  // moment it is needed, using **each page's own** matrix.
  //
  // DXF has no concept of pages, so a multi-page drawing would otherwise stack
  // every sheet at overlapping coordinates with no way to tell them apart. Each
  // page is therefore written to its own layer.
  b.tag(0, 'SECTION').tag(2, 'ENTITIES');
  for (const page of doc.pages) {
    for (const e of page.entities) {
      const moved = toDrawingSpace(e, page.transform);
      writeEntity(
        b,
        { ...moved, layer: layerFor(doc, page.index, e.layer) },
        modelSpaceHandle,
        textStyle,
        // From the UNMOVED entity: the box and the height share a space there.
        { lineSpacing: lineSpacingRatioOf(e), fitAdvance: fitAdvanceRatioOf(e) },
      );
    }
    // Through the SAME page transform as everything else, which is the whole
    // reason the outlines arrive in page space rather than pre-placed. A second
    // implementation of that transform would be a second thing to get wrong, and
    // the failure would be outlines sitting near their text instead of on it.
    for (const outline of options.textOutlines?.get(page.index) ?? []) {
      const moved = toDrawingSpace(outline, page.transform);
      writeEntity(
        b,
        { ...moved, layer: layerFor(doc, page.index, TEXT_OUTLINE_LAYER) },
        modelSpaceHandle,
        textStyle,
        { lineSpacing: lineSpacingRatioOf(outline), fitAdvance: fitAdvanceRatioOf(outline) },
      );
    }
  }
  b.tag(0, 'ENDSEC');

  writeObjects(b, rootDictHandle, groupDictHandle);
  b.tag(0, 'EOF');
  return b.toString();
}

/**
 * The never-guess rule, guarded at runtime as well as in the type system.
 *
 * The type signature is the primary defence, but a cast defeats it — and this
 * function is the one place where a wrong answer is worse than no answer, so it
 * does not rely on a single mechanism. A DXF at the wrong scale looks correct.
 */
export function assertScaleResolved(doc: ResolvedDocument): void {
  if (doc.scaleState !== 'resolved') {
    throw new Error(
      'refusing to write a DXF: physical scale is not resolved. ' +
        'A DXF at the wrong scale is worse than no DXF.',
    );
  }
  if (!(typeof doc.mmPerPoint === 'number') || !(doc.mmPerPoint > 0)) {
    throw new Error(
      `refusing to write a DXF: scale is marked resolved but mmPerPoint is ${String(doc.mmPerPoint)}`,
    );
  }
  for (const page of doc.pages) {
    if (!page.transform || page.transform.length !== 6) {
      throw new Error(
        `refusing to write a DXF: page ${page.index} carries no coordinate transform`,
      );
    }
  }
}

/** Multi-page drawings are separated by layer, since DXF has no pages. */
function layerFor(doc: ResolvedDocument, pageIndex: number, layer: string): string {
  if (doc.pages.length <= 1) return layer;
  return layer === '0' ? `PAGE_${pageIndex + 1}` : `PAGE_${pageIndex + 1}_${layer}`;
}

function collectLayers(
  doc: ResolvedDocument,
  textOutlines?: ReadonlyMap<number, readonly PolylineEntity[]>,
): string[] {
  const names = new Set<string>(['0']);
  for (const page of doc.pages) {
    for (const e of page.entities) names.add(layerFor(doc, page.index, e.layer));
    // Declared as well as referenced. An entity on a layer the table does not
    // list is an audit fix, and this writer's claim is zero errors AND zero
    // fixes.
    if ((textOutlines?.get(page.index)?.length ?? 0) > 0) {
      names.add(layerFor(doc, page.index, TEXT_OUTLINE_LAYER));
    }
  }
  return [...names].sort();
}

function writeHeader(b: Builder): void {
  b.tag(0, 'SECTION').tag(2, 'HEADER');
  b.tag(9, '$ACADVER').tag(1, DXF_VERSION);
  b.tag(9, '$HANDSEED').tag(5, 'FFFFFF');
  b.tag(9, '$INSUNITS').tag(70, INSUNITS_MILLIMETRES);
  b.tag(9, '$MEASUREMENT').tag(70, MEASUREMENT_METRIC);
  b.tag(9, '$LUNITS').tag(70, 2);
  b.tag(9, '$LUPREC').tag(70, 4);
  b.tag(9, '$AUNITS').tag(70, 0);
  b.tag(9, '$AUPREC').tag(70, 4);
  b.tag(0, 'ENDSEC');
}

function writeClasses(b: Builder): void {
  b.tag(0, 'SECTION').tag(2, 'CLASSES').tag(0, 'ENDSEC');
}

function writeTables(
  b: Builder,
  layers: string[],
  modelSpaceHandle: string,
  paperSpaceHandle: string,
  textStyle: DxfTextStyle,
): void {
  b.tag(0, 'SECTION').tag(2, 'TABLES');

  // VPORT
  const vportTable = b.nextHandle();
  b.tag(0, 'TABLE').tag(2, 'VPORT').tag(5, vportTable).tag(100, 'AcDbSymbolTable').tag(70, 1);
  b.tag(0, 'VPORT').tag(5, b.nextHandle()).tag(330, vportTable)
    .tag(100, 'AcDbSymbolTableRecord').tag(100, 'AcDbViewportTableRecord')
    .tag(2, '*Active').tag(70, 0)
    .coord(10, 0).coord(20, 0).coord(11, 1).coord(21, 1)
    .coord(12, 0).coord(22, 0).coord(13, 0).coord(23, 0)
    .coord(14, 10).coord(24, 10).coord(15, 10).coord(25, 10)
    .coord(16, 0).coord(26, 0).coord(36, 1)
    .coord(17, 0).coord(27, 0).coord(37, 0)
    .coord(40, 297).coord(41, 1.9).coord(42, 50).coord(43, 0).coord(44, 0)
    .coord(50, 0).coord(51, 0)
    .tag(71, 0).tag(72, 100).tag(73, 1).tag(74, 3).tag(75, 0).tag(76, 0).tag(77, 0).tag(78, 0);
  b.tag(0, 'ENDTAB');

  // LTYPE — ByBlock, ByLayer and Continuous are expected to exist.
  const ltypeTable = b.nextHandle();
  b.tag(0, 'TABLE').tag(2, 'LTYPE').tag(5, ltypeTable).tag(100, 'AcDbSymbolTable').tag(70, 3);
  for (const [name, description] of [
    ['ByBlock', ''],
    ['ByLayer', ''],
    ['Continuous', 'Solid line'],
  ] as const) {
    b.tag(0, 'LTYPE').tag(5, b.nextHandle()).tag(330, ltypeTable)
      .tag(100, 'AcDbSymbolTableRecord').tag(100, 'AcDbLinetypeTableRecord')
      .tag(2, name).tag(70, 0).tag(3, description).tag(72, 65).tag(73, 0).coord(40, 0);
  }
  b.tag(0, 'ENDTAB');

  // LAYER
  const layerTable = b.nextHandle();
  b.tag(0, 'TABLE').tag(2, 'LAYER').tag(5, layerTable).tag(100, 'AcDbSymbolTable').tag(70, layers.length);
  for (const name of layers) {
    b.tag(0, 'LAYER').tag(5, b.nextHandle()).tag(330, layerTable)
      .tag(100, 'AcDbSymbolTableRecord').tag(100, 'AcDbLayerTableRecord')
      .tag(2, name).tag(70, 0).tag(62, 7).tag(6, 'Continuous').tag(370, -3);
  }
  b.tag(0, 'ENDTAB');

  // STYLE
  const styleTable = b.nextHandle();
  const styles = [STANDARD_TEXT_STYLE, textStyle];
  b.tag(0, 'TABLE').tag(2, 'STYLE').tag(5, styleTable).tag(100, 'AcDbSymbolTable').tag(70, styles.length);
  for (const style of styles) {
    b.tag(0, 'STYLE').tag(5, b.nextHandle()).tag(330, styleTable)
      .tag(100, 'AcDbSymbolTableRecord').tag(100, 'AcDbTextStyleTableRecord')
      .tag(2, style.name).tag(70, 0).coord(40, 0).coord(41, 1).coord(50, 0)
      .tag(71, 0).coord(42, 2.5).tag(3, style.font).tag(4, style.bigFont);
    // AutoCAD resolves a TrueType style by typeface, not by the file name in
    // group 3, and falls back to its alternate stroke font when it cannot. The
    // alternate has no CJK glyphs either, so omitting this reintroduces the
    // exact bug this style exists to fix. Registered under the ACAD APPID,
    // which the table above already declares.
    if (style.typeface !== '') {
      b.tag(1001, 'ACAD').tag(1000, style.typeface).tag(1071, extendedFontFlags(style));
    }
  }
  b.tag(0, 'ENDTAB');

  for (const empty of ['VIEW', 'UCS'] as const) {
    b.tag(0, 'TABLE').tag(2, empty).tag(5, b.nextHandle()).tag(100, 'AcDbSymbolTable').tag(70, 0);
    b.tag(0, 'ENDTAB');
  }

  // APPID
  const appidTable = b.nextHandle();
  b.tag(0, 'TABLE').tag(2, 'APPID').tag(5, appidTable).tag(100, 'AcDbSymbolTable').tag(70, 1);
  b.tag(0, 'APPID').tag(5, b.nextHandle()).tag(330, appidTable)
    .tag(100, 'AcDbSymbolTableRecord').tag(100, 'AcDbRegAppTableRecord').tag(2, 'ACAD').tag(70, 0);
  b.tag(0, 'ENDTAB');

  // DIMSTYLE — note the record handle uses group 105, not 5.
  const dimstyleTable = b.nextHandle();
  b.tag(0, 'TABLE').tag(2, 'DIMSTYLE').tag(5, dimstyleTable).tag(100, 'AcDbSymbolTable').tag(70, 1)
    .tag(100, 'AcDbDimStyleTable').tag(71, 0);
  b.tag(0, 'DIMSTYLE').tag(105, b.nextHandle()).tag(330, dimstyleTable)
    .tag(100, 'AcDbSymbolTableRecord').tag(100, 'AcDbDimStyleTableRecord')
    .tag(2, 'Standard').tag(70, 0);
  b.tag(0, 'ENDTAB');

  // BLOCK_RECORD
  const blockRecordTable = b.nextHandle();
  b.tag(0, 'TABLE').tag(2, 'BLOCK_RECORD').tag(5, blockRecordTable).tag(100, 'AcDbSymbolTable').tag(70, 2);
  for (const [handle, name] of [
    [modelSpaceHandle, '*Model_Space'],
    [paperSpaceHandle, '*Paper_Space'],
  ] as const) {
    b.tag(0, 'BLOCK_RECORD').tag(5, handle).tag(330, blockRecordTable)
      .tag(100, 'AcDbSymbolTableRecord').tag(100, 'AcDbBlockTableRecord')
      .tag(2, name).tag(70, 0).tag(280, 1).tag(281, 0);
  }
  b.tag(0, 'ENDTAB');

  b.tag(0, 'ENDSEC');
}

function writeBlocks(b: Builder, modelSpaceHandle: string, paperSpaceHandle: string): void {
  b.tag(0, 'SECTION').tag(2, 'BLOCKS');
  for (const [handle, name] of [
    [modelSpaceHandle, '*Model_Space'],
    [paperSpaceHandle, '*Paper_Space'],
  ] as const) {
    b.tag(0, 'BLOCK').tag(5, b.nextHandle()).tag(330, handle)
      .tag(100, 'AcDbEntity').tag(8, '0').tag(100, 'AcDbBlockBegin')
      .tag(2, name).tag(70, 0).coord(10, 0).coord(20, 0).coord(30, 0)
      .tag(3, name).tag(1, '');
    b.tag(0, 'ENDBLK').tag(5, b.nextHandle()).tag(330, handle)
      .tag(100, 'AcDbEntity').tag(8, '0').tag(100, 'AcDbBlockEnd');
  }
  b.tag(0, 'ENDSEC');
}

function writeObjects(b: Builder, rootDictHandle: string, groupDictHandle: string): void {
  b.tag(0, 'SECTION').tag(2, 'OBJECTS');
  // The root dictionary owns itself to nothing: its owner handle is the literal
  // '0'. Omitting it is reported as an invalid owner handle and silently
  // repaired on load, which would mean shipping a file that only works because
  // the reader fixed it.
  b.tag(0, 'DICTIONARY').tag(5, rootDictHandle).tag(330, '0')
    .tag(100, 'AcDbDictionary').tag(281, 1)
    .tag(3, 'ACAD_GROUP').tag(350, groupDictHandle);
  b.tag(0, 'DICTIONARY').tag(5, groupDictHandle).tag(330, rootDictHandle)
    .tag(100, 'AcDbDictionary').tag(281, 1);
  b.tag(0, 'ENDSEC');
}

/**
 * Extrusion is written explicitly on every entity.
 *
 * A structural audit never checks it, and a mirrored transform silently leaves
 * it at (0,0,-1) — world geometry stays correct while every consumer reading a
 * raw centre or angle attribute gets nonsense.
 */
function extrusion(b: Builder): void {
  b.coord(210, 0).coord(220, 0).coord(230, 1);
}

function common(b: Builder, e: DrawingEntity, owner: string, subclass: string): void {
  b.tag(0, dxfTypeOf(e));
  b.tag(5, b.nextHandle()).tag(330, owner).tag(100, 'AcDbEntity').tag(8, e.layer);
  // Colour is written in both the legacy index form and the modern true-colour
  // form. Writing only one of them is a CAD compatibility trap: some readers
  // honour 62 and ignore 420, and some do the reverse.
  b.tag(62, 7).tag(420, trueColour(e.colour));
  b.tag(370, snapLineweight(e.strokeWidth));
  b.tag(100, subclass);
}

function dxfTypeOf(e: DrawingEntity): string {
  return e.type;
}

function trueColour(hex: string): number {
  const v = Number.parseInt(hex.replace(/^#/, ''), 16);
  return Number.isFinite(v) ? v & 0xffffff : 0;
}

/**
 * Ratios taken from an entity BEFORE it was moved into drawing space.
 *
 * Both are dimensionless on purpose. `source.bbox` stays in page points while a
 * moved entity's height is millimetres -- provenance keeps the space it came
 * from -- so a ratio is the only form in which a page-space measurement can
 * cross into drawing space without carrying a scale factor that would itself
 * have to be right.
 *
 * Named rather than passed as two trailing nullable numbers, because two of
 * those in a row is an argument-order defect waiting to happen and neither
 * order would fail to compile.
 */
interface PageSpaceRatios {
  /** Pitch over height. See `lineSpacingRatioOf`. */
  readonly lineSpacing: number | null;
  /** Measured advance over height. See `fitAdvanceRatioOf`. */
  readonly fitAdvance: number | null;
}

const NO_RATIOS: PageSpaceRatios = { lineSpacing: null, fitAdvance: null };

/** Group 72 = 5: set the string between groups 10/20 and 11/21 at the given height. */
export const FIT_BETWEEN_POINTS = 5;

function writeEntity(
  b: Builder,
  e: DrawingEntity,
  owner: string,
  textStyle: DxfTextStyle,
  ratios: PageSpaceRatios = NO_RATIOS,
): void {
  switch (e.type) {
    case 'LINE': {
      common(b, e, owner, 'AcDbLine');
      b.coord(10, e.start.x).coord(20, e.start.y).coord(30, 0);
      b.coord(11, e.end.x).coord(21, e.end.y).coord(31, 0);
      extrusion(b);
      break;
    }
    case 'LWPOLYLINE': {
      common(b, e, owner, 'AcDbPolyline');
      b.tag(90, e.vertices.length).tag(70, e.closed ? 1 : 0).coord(43, 0);
      e.vertices.forEach((v, i) => {
        b.coord(10, v.x).coord(20, v.y);
        const bulge = e.bulges[i] ?? 0;
        if (bulge !== 0) b.coord(42, bulge);
      });
      extrusion(b);
      break;
    }
    case 'CIRCLE': {
      common(b, e, owner, 'AcDbCircle');
      b.coord(10, e.centre.x).coord(20, e.centre.y).coord(30, 0).coord(40, e.radius);
      extrusion(b);
      break;
    }
    case 'ARC': {
      common(b, e, owner, 'AcDbCircle');
      b.coord(10, e.centre.x).coord(20, e.centre.y).coord(30, 0).coord(40, e.radius);
      extrusion(b);
      b.tag(100, 'AcDbArc').coord(50, e.startAngle).coord(51, e.endAngle);
      break;
    }
    case 'TEXT': {
      common(b, e, owner, 'AcDbText');
      b.coord(10, e.anchor.x).coord(20, e.anchor.y).coord(30, 0);
      // Group 40 is a cap height; `e.height` is the PDF's em. See CAP_HEIGHT_RATIO.
      b.coord(40, e.height * CAP_HEIGHT_RATIO).tag(1, escapeText(e.text));
      b.coord(50, e.rotation).coord(41, 1).coord(51, 0).tag(7, textStyle.name);
      /*
        Fit, whenever the advance is a measurement.

        A DXF names a font; it cannot carry one, so the width a string ends up
        with belongs to whatever the reader substitutes. `CJK_TEXT_STYLE` names
        MS Gothic, whose Latin is half-width monospace; a reader falling back to
        a reader falling back to Hiragino sets F01's own title fields 3.4 % to
        8.5 % wide -- Hiragino Sans and Hiragino Kaku Gothic ProN are distinct faces
        with byte-identical advances, measured. Group 41
        is no answer to that -- it is a factor against the same unknown font.

        Group 72 = 5 says: set this string between these two points at this
        height. The reader fits it with the font it actually has, which makes the
        extent right whatever that font turns out to be. It is the DXF's
        `textLength`, and the overlay imposes the identical measurement through
        `lengthAdjust="spacingAndGlyphs"`.
      */
      const fit = ratios.fitAdvance;
      const along = fit === null ? 0 : fit * e.height;
      const rad = (e.rotation * Math.PI) / 180;
      b.tag(71, 0).tag(72, fit === null ? 0 : FIT_BETWEEN_POINTS);
      b.coord(11, e.anchor.x + Math.cos(rad) * along)
        .coord(21, e.anchor.y + Math.sin(rad) * along)
        .coord(31, 0);
      extrusion(b);
      // TEXT carries its subclass marker a second time before the vertical
      // justification group. Omitting it makes readers misassign group 73.
      b.tag(100, 'AcDbText').tag(73, 0);
      break;
    }
    case 'MTEXT': {
      common(b, e, owner, 'AcDbMText');
      // Rotation first: the insertion point is offset along the text's own up
      // direction, so it needs the same angle the direction vector states.
      const rad = (e.rotation * Math.PI) / 180;

      /*
        Group 71 is an attachment point — a CORNER of the text box — and `anchor`
        is the FIRST line's baseline (`extract/text.ts`). Those are the same point
        only for a one-line block, and a one-line block is written as `TEXT`
        (`block.length > 1 ? 'MTEXT' : 'TEXT'`), so every MTEXT here is
        multi-line.

        This declared **7, bottom left**, and handed it the first baseline. On
        fixture F01 that put the insertion point at y 113.556 where bottom-left
        needs the last baseline at 110.098: **3.457 mm too high, one line pitch
        exactly**, on every note in every drawing. The single-line `TEXT` beside
        it is anchored at a true baseline (`halign 0, valign 0`), so the notes
        floated a line above the text they were drawn level with.

        **Top left** is the corner to declare, because the top of the first line
        is the one point a block's position can be stated from without knowing
        how many lines follow it or what pitch the reader will choose for them.
        It is one text height above the baseline, measured along the text's own
        up direction — `(-sin, cos)` to the direction vector's `(cos, sin)` —
        because adding it to world y would put a rotated note a line to the side
        instead of a line too high, which is the same defect wearing a hat.
      */
      const up = { x: -Math.sin(rad), y: Math.cos(rad) };
      // One CAP height, not one em: top-left attachment means the top of the first
      // line, and to a reader that line is `cap` tall. See CAP_HEIGHT_RATIO.
      const cap = e.height * CAP_HEIGHT_RATIO;
      b.coord(10, e.anchor.x + up.x * cap)
        .coord(20, e.anchor.y + up.y * cap)
        .coord(30, 0);
      b.coord(40, cap).coord(41, 0);
      b.tag(71, 1).tag(72, 1); // 1 = top left

      /*
        The distance between the lines, stated rather than left to the reader.

        `drawing.json` measures it and the overlay draws to it; this file said
        nothing, so a reader applied its own default. That default is 3-on-5
        spacing — 5/3 = 1.667 of the cap height, confirmed in ezdxf's own
        implementation of the spec, whose `leading(cap_height, factor)` returns
        `cap_height * 1.667 * factor`. On fixture F01 the producer's pitch is
        1.4000 cap heights, so the notes went out **19 % further apart** than the
        drawing has them.

        Group 44 is that factor and 73 = 2 is "exact", so a tall glyph cannot
        override the spacing the drawing actually used. The ratio is computed in
        page space, where the pitch and the height are both still in points, so it
        needs no scale — a ratio is the one quantity a transform cannot change.

        Omitted, not clamped, when it falls outside the 0.25–4.00 the format allows:
        the nearest legal value would state a spacing the drawing does not have, and
        the pitch is still recoverable from `source.bbox`.
      */
      const ratio = ratios.lineSpacing;
      if (ratio !== null) {
        // `ratio` is pitch over EM; 3-on-5 counts CAP heights, so divide by the
        // ratio between them as well.
        b.tag(73, 2).coord(44, ratio / CAP_HEIGHT_RATIO / THREE_ON_FIVE);
      }
      writeMTextString(b, e.text);
      b.tag(7, textStyle.name);
      extrusion(b);
      // Group 50 on MTEXT is documented as radians but read as degrees by common
      // implementations, so the x-axis vector is the only statement of rotation.
      b.coord(11, Math.cos(rad)).coord(21, Math.sin(rad)).coord(31, 0);
      break;
    }
  }
}

/**
 * The cap height of a font as a fraction of its em.
 *
 * **Group 40 is a CAP height and a PDF gives an EM.** A reader draws a string
 * whose capitals are exactly group 40 tall — that is what "text height" means in
 * AutoCAD, and ezdxf agrees in two places: `render/abstract_mtext_renderer.py`
 * sets `ctx.cap_height = char_height`, and `tools/text_size.py` does the same for
 * TEXT. Putting the em there made every string **1 / 0.716 = 1.40 times too
 * tall**.
 *
 * Measured from the real font files rather than from a raster, since a rendered
 * glyph carries stroke and hinting that a metric does not:
 *
 *     Helvetica.ttc      cap/em 0.7173     (the face F01 names)
 *     Arial.ttf          cap/em 0.7158
 *     Arial Unicode.ttf  cap/em 0.7158
 *
 * One constant, because the exact ratio belongs to whichever font the reader has
 * and no DXF can pin it — the same limit as glyph widths. 0.716 is the standard
 * figure for the sans faces these presets name and is within a per cent of every
 * candidate on the machine this was measured on. The residual is a per cent; the
 * error it replaces was thirty-nine.
 *
 * Proof the em was wrong is inside the file: with `--outline-text` the same row of
 * glyphs is drawn as real polylines 6.83 pt tall while its TEXT entity claimed
 * 9.00 — one DXF disagreeing with itself by thirty per cent.
 */
export const CAP_HEIGHT_RATIO = 0.716;

/** A DXF reader's default line spacing: 3-on-5, i.e. 5/3 of the cap height. */
const THREE_ON_FIVE = 1.667;

/** Group 44's legal range. */
const MIN_LINE_SPACING_FACTOR = 0.25;
const MAX_LINE_SPACING_FACTOR = 4;

/**
 * The pitch of a multi-line block as a multiple of its own text height.
 *
 * Taken from the entity **before** it is moved into drawing space, because there
 * the box and the height are both still in page points and the ratio needs no
 * scale factor. After the move the height is millimetres and `source.bbox` is not
 * — provenance stays in the space it came from — and mixing the two would be an
 * error no test of the writer alone could see.
 *
 * The box spans one text height above the first baseline plus one pitch for each
 * line after the first: `|y1 - y0| = height + (n - 1) * pitch`. Null when there is
 * only one line, when the box says nothing, or when the answer falls outside the
 * 0.25–4.00 that group 44 can carry.
 */
/**
 * A single-line string's measured advance over its height, or null.
 *
 * Null for anything that cannot honestly be fitted:
 *
 *  - **MTEXT.** Fit is a TEXT construction and takes one extent, and a block's
 *    lines do not share one -- F01's notes measure 25.28 pt and 122.15 pt.
 *  - **An estimated advance.** Fitting a string between two points a guess apart
 *    would move its glyphs to match the guess. The overlay declines for the same
 *    reason and on the same test, so the two surfaces agree about which strings
 *    carry a measurement.
 *  - **A degenerate height**, which would make the ratio meaningless.
 */
function fitAdvanceRatioOf(e: DrawingEntity): number | null {
  if (e.type !== 'TEXT') return null;
  if (!(e.height > 0)) return null;
  // No second guard on the advance itself: `advanceIsEstimated` is false only
  // when the measured box is wider than `height * 0.05`, so reaching here means
  // `advanceOf` returns that measurement and it is positive. A `> 0` test after
  // this point can never be false, and an unfalsifiable branch reads as a check
  // while checking nothing.
  if (advanceIsEstimated(e)) return null;
  return advanceOf(e) / e.height;
}

function lineSpacingRatioOf(e: DrawingEntity): number | null {
  if (e.type !== 'TEXT' && e.type !== 'MTEXT') return null;
  const lines = e.text.split(/\r?\n/).length;
  if (lines < 2) return null;
  if (!(e.height > 0)) return null;
  // The pitch the producer used, as the entity carries it. Recovered from
  // `source.bbox` until that box turned out to be a union in page axes while a
  // run's width and height are along its own -- see `TextEntity.linePitch`.
  const pitch = e.linePitch;
  if (pitch === undefined || !(pitch > 0)) return null;
  const factor = pitch / e.height / CAP_HEIGHT_RATIO / THREE_ON_FIVE;
  if (factor < MIN_LINE_SPACING_FACTOR || factor > MAX_LINE_SPACING_FACTOR) return null;
  return pitch / e.height;
}

/**
 * DXF string groups hold at most 255 **bytes**, and an R2018 file is UTF-8.
 *
 * That distinction is the whole point of this function. A Japanese character
 * costs three bytes, so a group fills up after roughly 85 of them, not 250 —
 * chunking by character emitted groups three times over the limit on exactly
 * the drawings this product exists for. Slicing at an arbitrary index was worse
 * than over-long: it could cut a surrogate pair in half and write a lone
 * surrogate, which is not encodable as UTF-8 at all.
 *
 * So text is walked by code point, each one escaped on its own, and a chunk is
 * closed before the byte limit rather than after it. Escaping per code point
 * also means no chunk can ever end between a backslash and the character it
 * escapes.
 */
const MAX_GROUP_BYTES = 250;

function writeMTextString(b: Builder, text: string): void {
  const chunks = chunkMText(normaliseNewlines(text));
  for (let i = 0; i < chunks.length - 1; i++) b.tag(3, chunks[i]!);
  b.tag(1, chunks[chunks.length - 1]!);
}

function chunkMText(text: string): string[] {
  const chunks: string[] = [];
  let current = '';
  let bytes = 0;
  for (const ch of text) {
    const piece = escapeMTextChar(ch);
    const width = utf8Length(piece);
    if (bytes + width > MAX_GROUP_BYTES && current !== '') {
      chunks.push(current);
      current = '';
      bytes = 0;
    }
    current += piece;
    bytes += width;
  }
  chunks.push(current);
  return chunks;
}

function utf8Length(s: string): number {
  let n = 0;
  for (const ch of s) {
    const cp = ch.codePointAt(0)!;
    n += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
  }
  return n;
}

/**
 * A bare carriage return would end the group's value line early and leave the
 * rest of the string masquerading as a group code, so every line ending becomes
 * a single `\n` before anything else looks at the text. PDF text extraction
 * does produce lone CRs.
 */
function normaliseNewlines(text: string): string {
  return text.replace(/\r\n|\r/g, '\n');
}

function escapeText(text: string): string {
  return normaliseNewlines(text).replace(/\n/g, ' ');
}

function escapeMTextChar(ch: string): string {
  if (ch === '\\') return '\\\\';
  if (ch === '{') return '\\{';
  if (ch === '}') return '\\}';
  if (ch === '\n') return '\\P';
  return ch;
}

/**
 * Determinism is defined on content, not on bytes — PRD Finding 3.
 *
 * DXF writers regenerate a document identifier on every write and embed
 * creation and update timestamps plus a version string containing the wall
 * clock. Hashing raw bytes would make a determinism check fail permanently, or
 * worse, be quietly removed. This strips the generated parts so two writes of
 * the same drawing hash the same.
 */
export function canonicalDxf(dxf: string): string {
  return dxf
    .replace(/\$VERSIONGUID[\s\S]{0,80}?\{[^}]*\}/g, '$VERSIONGUID')
    .replace(/\$TD(CREATE|UPDATE|INDWG|USRTIMER)[\s\S]{0,40}?\n\s*40\s*\n\s*[-\d.eE+]+/g, '$TDSTRIPPED')
    .replace(/\$FINGERPRINTGUID[\s\S]{0,80}?\{[^}]*\}/g, '$FINGERPRINTGUID')
    .replace(/\r\n/g, '\n');
}
